import { useState } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import { toast } from "sonner"
import { Save, Send } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useForm, Controller } from "react-hook-form"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { EmailConfigResponse, UpsertEmailConfigInput } from "@fohet/caravan-booker-shared/types"
import { inputClass, labelClass, sectionClass, primaryBtnClass, sectionHeaderClass } from "@/lib/styleClasses"
import { PasswordInput } from "@/components/admin/PasswordInput"

type AuthType = "none" | "password" | "oauth2"

interface EmailConfigFormValues {
    fromName: string
    fromEmail: string
    smtpHost: string
    smtpPort: string
    authType: AuthType
    user: string
    pass: string
    clientId: string
    clientSecret: string
    refreshToken: string
    emailHeaderHtml: string
    emailFooterHtml: string
}

export const EmailConfigSection = ({ arealId }: { arealId: string }) => {
    const { t } = useTranslation()
    const [testTo, setTestTo] = useState("")

    const { data: config, isLoading } = useQuery({
        queryKey: ["admin", "email-config", arealId],
        queryFn: () => api.get<EmailConfigResponse>(`/areals/${arealId}/email-config`),
        enabled: !!arealId,
    })

    const { register, handleSubmit, control, watch } = useForm<EmailConfigFormValues>({
        values: config
            ? {
                  fromName: config.fromName ?? "",
                  fromEmail: config.fromEmail ?? "",
                  smtpHost: config.smtpHost ?? "",
                  smtpPort: config.smtpPort != null ? String(config.smtpPort) : "587",
                  authType: (config.smtpAuthType as AuthType | null) ?? "none",
                  user: config.smtpUser ?? "",
                  pass: "",
                  clientId: "",
                  clientSecret: "",
                  refreshToken: "",
                  emailHeaderHtml: config.emailHeaderHtml ?? "",
                  emailFooterHtml: config.emailFooterHtml ?? "",
              }
            : undefined,
    })

    const authType = watch("authType")

    const saveMutation = useMutation({
        mutationFn: (values: EmailConfigFormValues) => {
            let smtpAuth: UpsertEmailConfigInput["smtpAuth"] = undefined
            if (values.authType === "none") {
                smtpAuth = null
            } else if (values.authType === "password" && values.pass) {
                smtpAuth = { type: "password", user: values.user, pass: values.pass }
            } else if (values.authType === "oauth2" && values.clientSecret) {
                smtpAuth = {
                    type: "oauth2",
                    user: values.user,
                    clientId: values.clientId,
                    clientSecret: values.clientSecret,
                    refreshToken: values.refreshToken,
                }
            }
            const body: UpsertEmailConfigInput = {
                fromName: values.fromName || null,
                fromEmail: values.fromEmail || null,
                smtpHost: values.smtpHost || null,
                smtpPort: values.smtpPort ? parseInt(values.smtpPort) : null,
                smtpAuth,
                emailHeaderHtml: values.emailHeaderHtml || null,
                emailFooterHtml: values.emailFooterHtml || null,
            }
            return api.put<EmailConfigResponse>(`/areals/${arealId}/email-config`, body)
        },
        onSuccess: () => toast.success(t("admin.settings.emailConfigToastSaved")),
        onError: (e) => {
            const msg = e instanceof ApiRequestError ? e.message : t("admin.settings.emailConfigToastFailed")
            if (msg.includes("Encryption")) {
                toast.error(t("admin.settings.emailConfigNoEncryption"))
            } else {
                toast.error(msg)
            }
        },
    })

    const testMutation = useMutation({
        mutationFn: () => api.post(`/areals/${arealId}/email-config/test`, { to: testTo }),
        onSuccess: () => toast.success(t("admin.settings.emailConfigTestToastSent")),
        onError: (e) =>
            toast.error(e instanceof ApiRequestError ? e.message : t("admin.settings.emailConfigTestToastFailed")),
    })

    const authTabClass = (type: AuthType) =>
        cn(
            "px-3 py-1.5 text-xs font-black rounded-xl transition-colors",
            authType === type ? "bg-charcoal text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200",
        )

    if (isLoading) return null

    return (
        <div className={sectionClass}>
            <h2 className={sectionHeaderClass}>{t("admin.settings.sectionEmailConfig")}</h2>
            <form onSubmit={handleSubmit((v) => saveMutation.mutate(v))} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>{t("admin.settings.emailConfigFromName")}</label>
                        <input {...register("fromName")} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.emailConfigFromEmail")}</label>
                        <input {...register("fromEmail")} type="email" className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.emailConfigSmtpHost")}</label>
                        <input {...register("smtpHost")} className={inputClass} placeholder="smtp.example.com" />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.emailConfigSmtpPort")}</label>
                        <input {...register("smtpPort")} type="number" min={1} max={65535} className={inputClass} />
                    </div>
                </div>
                <div>
                    <label className={labelClass}>{t("admin.settings.emailConfigAuthType")}</label>
                    <Controller
                        name="authType"
                        control={control}
                        render={({ field }) => (
                            <div className="flex gap-2 mt-1">
                                {(["none", "password", "oauth2"] as AuthType[]).map((type) => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => field.onChange(type)}
                                        className={authTabClass(type)}
                                    >
                                        {type === "none"
                                            ? t("admin.settings.emailConfigAuthNone")
                                            : type === "password"
                                              ? t("admin.settings.emailConfigAuthPassword")
                                              : t("admin.settings.emailConfigAuthOAuth2")}
                                    </button>
                                ))}
                            </div>
                        )}
                    />
                </div>
                {authType === "password" && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>{t("admin.settings.emailConfigUser")}</label>
                            <input {...register("user")} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>{t("admin.settings.emailConfigPass")}</label>
                            <PasswordInput {...register("pass")} placeholder={config?.hasCredentials ? "••••••••" : ""} />
                            {config?.hasCredentials && (
                                <p className="text-xs text-slate-500 mt-1">{t("admin.settings.emailConfigHasCredentials")}</p>
                            )}
                        </div>
                    </div>
                )}
                {authType === "oauth2" && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>{t("admin.settings.emailConfigUser")}</label>
                            <input {...register("user")} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>{t("admin.settings.emailConfigClientId")}</label>
                            <input {...register("clientId")} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>{t("admin.settings.emailConfigClientSecret")}</label>
                            <PasswordInput {...register("clientSecret")} placeholder={config?.hasCredentials ? "••••••••" : ""} />
                        </div>
                        <div>
                            <label className={labelClass}>{t("admin.settings.emailConfigRefreshToken")}</label>
                            <PasswordInput {...register("refreshToken")} placeholder={config?.hasCredentials ? "••••••••" : ""} />
                            {config?.hasCredentials && (
                                <p className="text-xs text-slate-500 mt-1">{t("admin.settings.emailConfigHasCredentials")}</p>
                            )}
                        </div>
                    </div>
                )}
                <div className="border-t border-slate-100 pt-4 space-y-4">
                    <div>
                        <label className={labelClass}>{t("admin.settings.emailConfigHeaderHtml")}</label>
                        <p className="text-xs text-slate-400 font-bold mb-1">{t("admin.settings.emailConfigHeaderHtmlHint")}</p>
                        <textarea
                            {...register("emailHeaderHtml")}
                            rows={3}
                            placeholder='<img src="https://..." alt="Logo" style="max-height:60px">'
                            className={cn(inputClass, "resize-y font-mono text-xs")}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.emailConfigFooterHtml")}</label>
                        <p className="text-xs text-slate-400 font-bold mb-1">{t("admin.settings.emailConfigFooterHtmlHint")}</p>
                        <textarea
                            {...register("emailFooterHtml")}
                            rows={3}
                            placeholder='<p style="color:#9ca3af;font-size:12px;margin:0">My Camp · Address · contact@camp.com</p>'
                            className={cn(inputClass, "resize-y font-mono text-xs")}
                        />
                    </div>
                </div>
                <button type="submit" disabled={saveMutation.isPending} className={primaryBtnClass}>
                    <Save className="size-3.5" />
                    {saveMutation.isPending ? t("admin.settings.emailConfigSaving") : t("admin.settings.emailConfigSave")}
                </button>
            </form>
            <div className="border-t border-slate-100 pt-4">
                <div className="flex gap-2 items-center flex-wrap">
                    <input
                        type="email"
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                        placeholder={t("admin.settings.emailConfigTestAddress")}
                        className={cn(inputClass, "flex-1 min-w-[200px]")}
                    />
                    <button
                        type="button"
                        onClick={() => testMutation.mutate()}
                        disabled={testMutation.isPending || !testTo}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-charcoal text-white font-black text-sm hover:bg-slate-700 transition-colors active:scale-95 disabled:opacity-50 shadow-md"
                    >
                        <Send className="size-3.5" />
                        {testMutation.isPending
                            ? t("admin.settings.emailConfigTestSending")
                            : t("admin.settings.emailConfigTestSend")}
                    </button>
                </div>
            </div>
        </div>
    )
}
